import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, CalendarRange, Pencil, X, Upload, GripVertical, MapPin, RotateCcw } from "lucide-react";
import { GAME_TYPES } from "../../lib/facilities";
import MapEmbed from "../../components/MapEmbed";
import TimePicker12, { fmt12 } from "../../components/TimePicker12";

const SPORTS_OPTS = ["Football", "Box Cricket", "Badminton", "Basketball", "Pickleball", "Padel"];
const MAX_PHOTOS = 15;
const MAX_BYTES = 10 * 1024 * 1024;

const EMPTY = {
    name: "", address: "", city: "",
    photo_url: "", photos: [],
    sports: [], rating: 4.5, distance_km: 0,
    open_time: "06:00", close_time: "23:00", active: true,
    latitude: null, longitude: null,
    map_embed_url: "",
    turfs: [],            // [{id?, name, sport, hourly_price}]  (only used in Edit-mode read)
    facilities: [],       // [{id?, name, game_type, turf_ids:[], hourly_price, sort_order}]
};

export default function AdminCentres() {
    const navigate = useNavigate();
    const [centres, setCentres] = useState([]);
    const [deleted, setDeleted] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        const [{ data }, delRes] = await Promise.all([
            api.get("/centres"),
            api.get("/admin/centres/deleted").catch(() => ({ data: [] })),
        ]);
        setCentres(data);
        setDeleted(delRes.data || []);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        // Seed two default turfs so the user can immediately link facilities
        setForm({
            ...EMPTY,
            turfs: [
                { id: `tmp_${Math.random().toString(36).slice(2, 9)}`, name: "Turf 1", sport: "Multi", hourly_price: 0 },
                { id: `tmp_${Math.random().toString(36).slice(2, 9)}`, name: "Turf 2", sport: "Multi", hourly_price: 0 },
            ],
        });
        setOpen(true);
    };

    const openEdit = async (c) => {
        // Fetch full centre (turfs + facilities)
        const { data: full } = await api.get(`/centres/${c.id}`);
        setEditing(full);
        setForm({
            name: full.name || "",
            address: full.address || "",
            city: full.city || "",
            photo_url: full.photo_url || "",
            photos: full.photos || [],
            sports: full.sports || [],
            rating: full.rating ?? 4.5,
            distance_km: full.distance_km ?? 0,
            open_time: full.open_time || "06:00",
            close_time: full.close_time || "23:00",
            active: full.active !== false,
            latitude: full.latitude ?? null,
            longitude: full.longitude ?? null,
            map_embed_url: full.map_embed_url || "",
            turfs: full.turfs || [],
            facilities: (full.facilities || []).map(f => ({
                ...f,
                turf_ids: [...(f.turf_ids || [])],
                // backfill peak/off-peak from legacy hourly_price for older centres
                price_before_5: f.price_before_5 ?? f.hourly_price ?? 0,
                price_after_5: f.price_after_5 ?? f.hourly_price ?? 0,
            })),
        });
        setOpen(true);
    };

    const handleFiles = async (files) => {
        const arr = Array.from(files || []);
        if (!arr.length) return;
        if (form.photos.length + arr.length > MAX_PHOTOS) {
            toast.error(`Max ${MAX_PHOTOS} photos. You already have ${form.photos.length}.`);
            return;
        }
        setUploading(true);
        const newUrls = [];
        for (const f of arr) {
            if (!["image/png", "image/jpeg", "image/jpg"].includes(f.type)) {
                toast.error(`${f.name}: only PNG / JPEG allowed`);
                continue;
            }
            if (f.size > MAX_BYTES) {
                toast.error(`${f.name}: exceeds 10 MB`);
                continue;
            }
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
                newUrls.push(data.url);
            } catch (e) {
                toast.error(`${f.name}: ${e.response?.data?.detail || "upload failed"}`);
            }
        }
        if (newUrls.length) {
            setForm(prev => ({
                ...prev,
                photos: [...prev.photos, ...newUrls],
                // Auto-promote the first uploaded photo to cover when no cover is set yet
                photo_url: prev.photo_url || newUrls[0],
            }));
            toast.success(`Uploaded ${newUrls.length} photo${newUrls.length === 1 ? "" : "s"}`);
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const removePhoto = (url) => setForm(prev => ({
        ...prev,
        photos: prev.photos.filter(p => p !== url),
        // If the removed photo was the cover, fall back to the first remaining gallery photo
        photo_url: prev.photo_url === url ? (prev.photos.find(p => p !== url) || "") : prev.photo_url,
    }));

    const onDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

    // Facility editor helpers
    const addFacility = () => {
        setForm(prev => ({
            ...prev,
            facilities: [...prev.facilities, {
                name: "", game_type: GAME_TYPES[0].value, turf_ids: [],
                price_before_5: 1500, price_after_5: 1700, hourly_price: 1500,
                sort_order: prev.facilities.length + 1,
            }],
        }));
    };
    const updateFacility = (idx, patch) => {
        setForm(prev => ({
            ...prev,
            facilities: prev.facilities.map((f, i) => i === idx ? { ...f, ...patch } : f),
        }));
    };
    const removeFacility = (idx) => {
        setForm(prev => ({ ...prev, facilities: prev.facilities.filter((_, i) => i !== idx) }));
    };
    const toggleTurfOnFacility = (idx, turfId) => {
        setForm(prev => ({
            ...prev,
            facilities: prev.facilities.map((f, i) => {
                if (i !== idx) return f;
                const has = f.turf_ids.includes(turfId);
                return { ...f, turf_ids: has ? f.turf_ids.filter(x => x !== turfId) : [...f.turf_ids, turfId] };
            }),
        }));
    };

    // Turf editor helpers
    const addTurf = () => {
        setForm(prev => ({
            ...prev,
            turfs: [...prev.turfs, {
                id: `tmp_${Math.random().toString(36).slice(2, 9)}`,
                name: `Turf ${prev.turfs.length + 1}`,
                sport: "Multi",
                hourly_price: 0,
            }],
        }));
    };
    const updateTurf = (idx, patch) => {
        setForm(prev => ({
            ...prev,
            turfs: prev.turfs.map((t, i) => i === idx ? { ...t, ...patch } : t),
        }));
    };
    const removeTurf = (idx) => {
        const turf = form.turfs[idx];
        if (!turf) return;
        const usedBy = form.facilities.filter(f => (f.turf_ids || []).includes(turf.id));
        if (usedBy.length > 0) {
            toast.error(`"${turf.name}" is still linked by ${usedBy.length} facility — unlink first`);
            return;
        }
        setForm(prev => ({ ...prev, turfs: prev.turfs.filter((_, i) => i !== idx) }));
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.name || !form.address || !form.city) {
            toast.error("Name, address and city are required"); return;
        }
        // Cover photo can come from either the URL field or the uploaded gallery (first photo auto-becomes cover)
        const effectiveCover = form.photo_url || (form.photos[0] || "");
        if (!effectiveCover) {
            toast.error("Upload at least one photo (it'll be used as cover) or paste a cover URL"); return;
        }
        if (form.sports.length === 0) { toast.error("Select at least one sport"); return; }
        for (const f of form.facilities) {
            if (!f.name) { toast.error("Each facility needs a name"); return; }
            const pBefore = parseInt(f.price_before_5);
            const pAfter = parseInt(f.price_after_5);
            if (!pBefore || pBefore <= 0) { toast.error(`"${f.name}": before-5pm price required`); return; }
            if (!pAfter || pAfter <= 0) { toast.error(`"${f.name}": after-5pm price required`); return; }
        }
        // Validate turf names
        for (const t of form.turfs) {
            if (!t.name || t.name.trim().length === 0) { toast.error("Each turf needs a name"); return; }
        }
        try {
            const payload = {
                name: form.name, address: form.address, city: form.city,
                photo_url: effectiveCover, photos: form.photos,
                sports: form.sports, rating: form.rating, distance_km: form.distance_km,
                open_time: form.open_time, close_time: form.close_time, active: form.active,
                latitude: form.latitude !== null && form.latitude !== undefined ? Number(form.latitude) : null,
                longitude: form.longitude !== null && form.longitude !== undefined ? Number(form.longitude) : null,
                map_embed_url: form.map_embed_url || null,
                turfs: form.turfs.map(t => ({
                    // strip temporary tmp_* IDs so backend assigns real UUIDs on first save
                    id: t.id && !t.id.startsWith("tmp_") ? t.id : undefined,
                    name: t.name.trim(),
                    sport: t.sport || "Multi",
                    hourly_price: parseInt(t.hourly_price) || 0,
                })),
                facilities: form.facilities.map((f, i) => {
                    const pBefore = parseInt(f.price_before_5) || 0;
                    const pAfter = parseInt(f.price_after_5) || 0;
                    return {
                        id: f.id, name: f.name, game_type: f.game_type,
                        turf_ids: f.turf_ids,
                        price_before_5: pBefore,
                        price_after_5: pAfter,
                        // keep legacy hourly_price in sync (fallback) — use before-5pm as base
                        hourly_price: pBefore,
                        sort_order: f.sort_order ?? (i + 1),
                    };
                }),
            };
            if (editing) {
                await api.put(`/centres/${editing.id}`, payload);
                toast.success("Centre updated");
            } else {
                await api.post("/centres", payload);
                toast.success("Centre created");
            }
            setOpen(false); setEditing(null); setForm(EMPTY); load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete this centre? You can restore it later from the Deleted section.")) return;
        await api.delete(`/centres/${id}`);
        toast.success("Centre deleted (recoverable)");
        load();
    };

    const restore = async (id) => {
        await api.post(`/centres/${id}/restore`);
        toast.success("Centre restored");
        load();
    };

    const toggleSport = (s) => setForm({
        ...form,
        sports: form.sports.includes(s) ? form.sports.filter(x => x !== s) : [...form.sports, s],
    });

    const photoSrc = (p) => p?.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`;

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Centres</h1>
                        <p className="text-slate-400 text-sm mt-1">Manage venues, photos, facilities (5v5/6v6/9v9/Box Cricket) and operating hours.</p>
                    </div>
                    <button onClick={openCreate} data-testid="new-centre-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                        <Plus className="w-4 h-4" /> New Centre
                    </button>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {centres.map(c => (
                        <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid={`admin-centre-${c.id}`}>
                            <div className="relative">
                                <img src={c.photo_url} alt={c.name} className="w-full h-32 object-cover" />
                                {(c.photos?.length > 0) && (
                                    <span className="absolute bottom-2 right-2 bg-slate-950/80 text-slate-200 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border border-slate-700">
                                        +{c.photos.length} photos
                                    </span>
                                )}
                            </div>
                            <div className="p-4">
                                <div className="font-display font-bold text-white uppercase">{c.name}</div>
                                <div className="text-xs text-slate-400 mt-0.5">{c.address}</div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                    {c.sports.map(s => <span key={s} className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">{s}</span>)}
                                </div>
                                <div className="mt-2 text-[11px] text-slate-500">
                                    {c.facilities?.length || 0} facilit{(c.facilities?.length || 0) === 1 ? "y" : "ies"}
                                </div>
                                <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                                    <span>★ {c.rating}</span>
                                    <span>{fmt12(c.open_time)} – {fmt12(c.close_time)}</span>
                                </div>
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    <button onClick={() => openEdit(c)} data-testid={`edit-centre-${c.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-8 bg-slate-200 text-slate-900 rounded text-[10px] font-bold uppercase tracking-widest hover:bg-white">
                                        <Pencil className="w-3 h-3" /> Edit
                                    </button>
                                    <button onClick={() => navigate(`/admin/slots?centre=${c.id}`)}
                                        data-testid={`manage-slots-${c.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-8 bg-sky-400 text-slate-950 rounded text-[10px] font-bold uppercase tracking-widest hover:bg-sky-300">
                                        <CalendarRange className="w-3 h-3" /> Slots
                                    </button>
                                    <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-[10px] font-bold uppercase tracking-widest" data-testid={`del-centre-${c.id}`}>
                                        <Trash2 className="w-3 h-3" /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {deleted.length > 0 && (
                    <div className="mt-10" data-testid="deleted-centres-section">
                        <h2 className="font-display text-lg font-bold uppercase text-slate-400 mb-3">Deleted Centres ({deleted.length})</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {deleted.map(c => (
                                <div key={c.id} className="bg-slate-900/60 border border-dashed border-slate-700 rounded-lg p-4 opacity-90"
                                     data-testid={`deleted-centre-${c.id}`}>
                                    <div className="font-display font-bold text-slate-300 uppercase">{c.name}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{c.address}, {c.city}</div>
                                    <div className="text-[10px] text-slate-600 mt-1">Deleted {c.deleted_at?.slice(0, 10) || ""}</div>
                                    <button onClick={() => restore(c.id)} data-testid={`restore-centre-${c.id}`}
                                        className="mt-3 inline-flex items-center gap-1.5 px-3 h-8 bg-[#00FF66] text-black rounded text-[10px] font-bold uppercase tracking-widest hover:bg-[#00cc52]">
                                        <RotateCcw className="w-3 h-3" /> Restore
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {open && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
                        <form onSubmit={save} className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4 my-6" data-testid="centre-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">{editing ? "Edit Centre" : "New Centre"}</h3>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Input label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="centre-name" />
                                <Input label="City" value={form.city} onChange={v => setForm({ ...form, city: v })} testid="centre-city" />
                            </div>
                            <Input label="Address" value={form.address} onChange={v => setForm({ ...form, address: v })} testid="centre-address" />

                            {/* MOD 24 — Manual Google Maps embed URL (no API key needed) */}
                            <div data-testid="centre-map-section" className="border border-slate-800 rounded p-3 space-y-3">
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 inline-flex items-center gap-2">
                                    <MapPin className="w-3 h-3 text-[#00FF66]" /> Map Embed URL
                                </label>
                                <p className="text-[11px] text-slate-500 -mt-1">
                                    Open <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" className="text-[#00FF66] underline">Google Maps</a>, search your centre,
                                    click <b className="text-slate-300">Share → Embed a map → Copy HTML</b>. Paste the whole snippet or just the URL here. Leave blank to hide the map.
                                </p>
                                <textarea value={form.map_embed_url}
                                    onChange={(e) => setForm({ ...form, map_embed_url: e.target.value })}
                                    placeholder='https://www.google.com/maps/embed?pb=...   OR   <iframe src="https://www.google.com/maps/embed?pb=..." ...></iframe>'
                                    rows={2} data-testid="centre-map-url-input"
                                    className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#00FF66] resize-y" />
                                {form.map_embed_url && form.map_embed_url.includes("google.com/maps") && (
                                    <div className="mt-2">
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Preview</div>
                                        <MapEmbed
                                            url={extractEmbedSrc(form.map_embed_url)}
                                            height={220}
                                            showDirections={false}
                                            testid="centre-map-preview"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Open Time</label>
                                    <div className="mt-1">
                                        <TimePicker12 value={form.open_time} onChange={(v) => setForm({ ...form, open_time: v })} testid="centre-open" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Close Time</label>
                                    <div className="mt-1">
                                        <TimePicker12 value={form.close_time} onChange={(v) => setForm({ ...form, close_time: v })} testid="centre-close" />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Input label="Rating" type="number" step="0.1" value={form.rating} onChange={v => setForm({ ...form, rating: parseFloat(v) || 0 })} testid="centre-rating" />
                                <Input label="Distance (km)" type="number" step="0.1" value={form.distance_km} onChange={v => setForm({ ...form, distance_km: parseFloat(v) || 0 })} testid="centre-distance" />
                            </div>

                            <Input label="Cover Photo URL" value={form.photo_url} onChange={v => setForm({ ...form, photo_url: v })} testid="centre-photo" optional />
                            <p className="-mt-1 text-[11px] text-slate-500">
                                Tip: upload photos below and click any thumbnail to set it as the cover — no need to paste a URL.
                                <br />Recommended cover size: <b className="text-slate-300">1920×700 px</b> (desktop) / <b className="text-slate-300">1080×1350 px</b> (mobile). Any image is accepted — the system auto-crops &amp; serves an optimized WebP per device.
                            </p>

                            {/* PHOTO UPLOADER */}
                            <div>
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
                                        Gallery Photos <span className="text-slate-500 normal-case tracking-normal ml-2">{form.photos.length} / {MAX_PHOTOS}</span>
                                    </label>
                                    <span className="text-[10px] text-slate-500 uppercase tracking-widest">PNG / JPEG · ≤10 MB each</span>
                                </div>
                                <div onDragOver={(e) => e.preventDefault()} onDrop={onDrop}
                                    className="mt-2 border-2 border-dashed border-slate-700 hover:border-[#00FF66]/60 transition-colors rounded-lg p-4 text-center cursor-pointer bg-slate-950"
                                    onClick={() => fileRef.current?.click()} data-testid="photo-dropzone">
                                    <Upload className="w-6 h-6 mx-auto text-slate-500" />
                                    <div className="text-sm text-slate-300 mt-1">{uploading ? "Uploading…" : "Click or drag PNG / JPEG files"}</div>
                                    <div className="text-[11px] text-slate-500">Up to {MAX_PHOTOS - form.photos.length} more</div>
                                    <input ref={fileRef} type="file" accept="image/png,image/jpeg" multiple className="hidden"
                                        onChange={(e) => handleFiles(e.target.files)} data-testid="photo-input" />
                                </div>
                                {form.photos.length > 0 && (
                                    <>
                                        <div className="mt-3 text-[11px] text-slate-500">
                                            Click any photo to set it as the cover. The cover is shown on the public centres list and the centre detail page.
                                        </div>
                                        <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
                                            {form.photos.map((p, i) => {
                                                const isCover = form.photo_url === p;
                                                return (
                                                    <button type="button" key={p}
                                                        onClick={() => setForm(prev => ({ ...prev, photo_url: p }))}
                                                        title={isCover ? "Current cover" : "Click to set as cover"}
                                                        data-testid={`gallery-photo-${i}`}
                                                        className={`relative group rounded overflow-hidden border-2 transition ${isCover ? "border-[#00FF66] ring-2 ring-[#00FF66]/40" : "border-slate-700 hover:border-[#00FF66]/60"}`}>
                                                        <img src={photoSrc(p)} alt="" className="w-full h-20 object-cover" />
                                                        <span onClick={(ev) => { ev.stopPropagation(); removePhoto(p); }}
                                                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-400 cursor-pointer z-10"
                                                            data-testid={`gallery-photo-remove-${i}`}>
                                                            <X className="w-3 h-3" />
                                                        </span>
                                                        {isCover ? (
                                                            <div className="absolute bottom-1 left-1 text-[9px] uppercase tracking-widest font-bold bg-[#00FF66] text-black px-1.5 rounded">Cover</div>
                                                        ) : (
                                                            <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/55 transition-colors flex items-center justify-center">
                                                                <span className="opacity-0 group-hover:opacity-100 text-[9px] uppercase tracking-widest font-bold bg-[#00FF66]/90 text-black px-1.5 py-0.5 rounded transition-opacity">
                                                                    Set as cover
                                                                </span>
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* SPORTS */}
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Sports</label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {SPORTS_OPTS.map(s => (
                                        <button key={s} type="button" onClick={() => toggleSport(s)} data-testid={`sport-toggle-${s.replace(/\s+/g, "-")}`}
                                            className={`px-3 h-8 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                                                form.sports.includes(s) ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-950 text-slate-300 border-slate-700"
                                            }`}>{s}</button>
                                    ))}
                                </div>
                            </div>

                            {/* TURFS — physical playing surfaces. Facilities link via turf_ids for interlocking. */}
                            <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/50" data-testid="turfs-section">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Turfs</label>
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            Add the physical playing surfaces here. Facilities (e.g. 6v6 Football, Box Cricket) below link to one or more turfs — bookings interlock automatically when they share a turf.
                                        </p>
                                    </div>
                                    <button type="button" onClick={addTurf} data-testid="add-turf-btn"
                                        className="inline-flex items-center gap-1 px-3 h-8 bg-sky-400 text-slate-950 rounded text-[10px] font-bold uppercase tracking-widest hover:bg-sky-300">
                                        <Plus className="w-3 h-3" /> Add Turf
                                    </button>
                                </div>
                                {form.turfs.length === 0 && (
                                    <p className="text-[11px] text-amber-400 mt-2">No turfs yet. Click <span className="font-bold">Add Turf</span> to start.</p>
                                )}
                                <div className="mt-3 space-y-2">
                                    {form.turfs.map((t, idx) => (
                                        <div key={t.id || idx} className="bg-slate-900 border border-slate-800 rounded p-3" data-testid={`turf-row-${idx}`}>
                                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                                                <div className="sm:col-span-6">
                                                    <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">Name</label>
                                                    <input value={t.name} onChange={(e) => updateTurf(idx, { name: e.target.value })}
                                                        placeholder={`Turf ${idx + 1}`} maxLength={60}
                                                        data-testid={`turf-name-${idx}`}
                                                        className="w-full mt-1 h-9 px-2 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                                </div>
                                                <div className="sm:col-span-4">
                                                    <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">Primary Sport</label>
                                                    <select value={t.sport || "Multi"} onChange={(e) => updateTurf(idx, { sport: e.target.value })}
                                                        data-testid={`turf-sport-${idx}`}
                                                        className="w-full mt-1 h-9 px-2 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                                        <option value="Multi">Multi-sport</option>
                                                        {SPORTS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                </div>
                                                <div className="sm:col-span-2 flex justify-end">
                                                    <button type="button" onClick={() => removeTurf(idx)} data-testid={`turf-del-${idx}`}
                                                        className="px-2 h-9 text-red-400 hover:text-red-300 border border-red-900 rounded inline-flex items-center justify-center">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                            {(() => {
                                                const linked = form.facilities.filter(f => (f.turf_ids || []).includes(t.id));
                                                if (linked.length === 0) {
                                                    return <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-600">Not linked to any facility yet</div>;
                                                }
                                                return (
                                                    <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-500">
                                                        Linked facilities:&nbsp;
                                                        {linked.map(f => f.name).filter(Boolean).join(" · ")}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* FACILITIES */}
                            <div className="border border-slate-800 rounded-lg p-4 bg-slate-950/50">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Facilities</label>
                                    <button type="button" onClick={addFacility} data-testid="add-facility-btn"
                                        className="inline-flex items-center gap-1 px-3 h-8 bg-[#00FF66] text-black rounded text-[10px] font-bold uppercase tracking-widest hover:bg-[#00cc52]">
                                        <Plus className="w-3 h-3" /> Add Facility
                                    </button>
                                </div>
                                {!editing && form.turfs.length === 0 && (
                                    <p className="text-[11px] text-amber-400 mt-2">
                                        Add at least one turf above before creating facilities.
                                    </p>
                                )}
                                {form.facilities.length === 0 && editing && (
                                    <p className="text-[11px] text-slate-500 mt-2">No facilities yet. Add one to expose it on the booking page.</p>
                                )}
                                <div className="mt-3 space-y-2">
                                    {form.facilities.map((f, idx) => (
                                        <div key={idx} className="bg-slate-900 border border-slate-800 rounded p-3 space-y-2" data-testid={`facility-row-${idx}`}>
                                            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                                                <div className="sm:col-span-1 flex items-center justify-center text-slate-600 pb-2"><GripVertical className="w-4 h-4" /></div>
                                                <div className="sm:col-span-3">
                                                    <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">Name</label>
                                                    <input value={f.name} onChange={(e) => updateFacility(idx, { name: e.target.value })}
                                                        placeholder="9v9 Football (Full Turf)"
                                                        data-testid={`fac-name-${idx}`}
                                                        className="w-full mt-1 h-9 px-2 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">Game Type</label>
                                                    <select value={f.game_type} onChange={(e) => updateFacility(idx, { game_type: e.target.value })}
                                                        data-testid={`fac-type-${idx}`}
                                                        className="w-full mt-1 h-9 px-2 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                                        {GAME_TYPES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                                                    </select>
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">₹/hour · Before 5 PM</label>
                                                    <input type="number" value={f.price_before_5 ?? ""} onChange={(e) => updateFacility(idx, { price_before_5: e.target.value })}
                                                        data-testid={`fac-price-before-${idx}`}
                                                        placeholder="Off-peak"
                                                        className="w-full mt-1 h-9 px-2 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                                </div>
                                                <div className="sm:col-span-2">
                                                    <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">₹/hour · After 5 PM</label>
                                                    <input type="number" value={f.price_after_5 ?? ""} onChange={(e) => updateFacility(idx, { price_after_5: e.target.value })}
                                                        data-testid={`fac-price-after-${idx}`}
                                                        placeholder="Peak"
                                                        className="w-full mt-1 h-9 px-2 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                                </div>
                                                <div className="sm:col-span-1">
                                                    <label className="text-[10px] font-bold tracking-widest uppercase text-slate-500">Order</label>
                                                    <input type="number" value={f.sort_order} onChange={(e) => updateFacility(idx, { sort_order: parseInt(e.target.value) || 0 })}
                                                        data-testid={`fac-order-${idx}`}
                                                        className="w-full mt-1 h-9 px-2 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                                </div>
                                                <div className="sm:col-span-12 -mt-2">
                                                    <div className="text-[10px] text-slate-500">
                                                        Half-hour rates shown to players: <span className="text-slate-300 font-bold">₹{Math.floor((parseInt(f.price_before_5) || 0) / 2)}</span> off-peak ·
                                                        <span className="text-slate-300 font-bold ml-1">₹{Math.floor((parseInt(f.price_after_5) || 0) / 2)}</span> peak
                                                    </div>
                                                </div>
                                                <div className="sm:col-span-1 flex justify-end pb-1">
                                                    <button type="button" onClick={() => removeFacility(idx)} data-testid={`fac-del-${idx}`}
                                                        className="text-red-400 hover:text-red-300">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            {form.turfs.length > 0 && (
                                                <div>
                                                    <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 mb-1">
                                                        Locked turfs (booking this facility will block these turfs at the chosen time)
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {form.turfs.map(t => (
                                                            <button key={t.id} type="button" onClick={() => toggleTurfOnFacility(idx, t.id)}
                                                                data-testid={`fac-${idx}-turf-${t.id}`}
                                                                className={`px-2 h-7 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                                                                    f.turf_ids.includes(t.id) ? "bg-sky-400 text-slate-950 border-sky-400" : "bg-slate-950 text-slate-400 border-slate-700"
                                                                }`}>{t.name}</button>
                                                        ))}
                                                    </div>
                                                    {f.turf_ids.length === 0 && (
                                                        <div className="mt-1 text-[10px] text-amber-400">Tip: select at least one turf — bookings here will block it.</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer" data-testid="centre-active-toggle">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#00FF66]" />
                                <span className="text-sm text-slate-200 font-bold uppercase tracking-widest">Active (visible to players)</span>
                            </label>

                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" data-testid="save-centre-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">
                                    {editing ? "Update Centre" : "Create Centre"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function Input({ label, type = "text", value, onChange, testid, step, optional }) {
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
                {label}{optional && <span className="ml-2 text-slate-600 normal-case tracking-normal">optional</span>}
            </label>
            <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} required={!optional} data-testid={testid}
                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
        </div>
    );
}

/** Extracts the iframe src URL when admin pastes an `<iframe ...>` snippet,
 *  otherwise returns the input as-is. Used for the live preview only. */
function extractEmbedSrc(raw) {
    if (!raw) return "";
    const s = raw.trim();
    if (s.startsWith("<iframe")) {
        const m = s.match(/src="([^"]+)"/);
        return m ? m[1] : "";
    }
    return s;
}
